import type { HttpClient } from "@/shared/api/http";
import type { ApiPagedResult } from "@tm-food/api-contract";
import {
  buildQueryString,
  mapApiPagedResult,
  toApiQueryParams,
  type ListQueryParams,
  type PagedResult,
} from "@/shared/list/listQueryTypes";

export type DocumentMasterRow = {
  id: string;
  docCode: string;
  titleVi: string;
  docTypeId: string;
  docKind: string;
  revision: string;
  status: string;
  orgUnitId: string | null;
  legacyCodes: string | null;
  systemFormKey: string | null;
  printBindingJson?: string | null;
};

export type DocFieldDef = {
  fieldKey: string;
  label: string;
  kind: string;
  seq: number;
  required: boolean;
  optionsJson: string | null;
  minValue: number | null;
  maxValue: number | null;
  step: number | null;
  uomLabel: string | null;
  requiredOnFail: boolean;
  failDependsOnFieldKey: string | null;
  sectionKey?: string | null;
  sectionLabel?: string | null;
};

export type DocumentVersionRow = {
  id: string;
  versionNo: number;
  status: string;
  effectiveFrom: string | null;
  fields: DocFieldDef[];
};

export type DocumentMasterDetail = DocumentMasterRow & {
  retentionYears: number;
  requiresAck: boolean;
  versions: DocumentVersionRow[];
};

export type FormInstanceSummary = {
  id: string;
  docCode: string;
  titleVi: string;
  plantCode: string;
  workDate: string;
  status: string;
  batchId: string | null;
  renderedPdfUri: string | null;
};

export type OrganizationProfile = {
  id: string;
  code: string;
  legalName: string;
  taxId: string | null;
  address: string | null;
  brandName: string | null;
  status: string;
  printFooterText: string | null;
  printLogoReadUri: string | null;
  bankName: string | null;
  bankBranch: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
};

export async function listDocumentMasters(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<DocumentMasterRow>> {
  const qs = buildQueryString(toApiQueryParams(params));
  const dto = await http.request<ApiPagedResult<DocumentMasterRow>>(
    `/api/v1/doc/masters${qs}`,
  );
  return mapApiPagedResult(dto, (r) => r);
}

export async function getDocumentMasterDetail(
  http: HttpClient,
  docCode: string,
): Promise<DocumentMasterDetail> {
  return http.request<DocumentMasterDetail>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/detail`,
  );
}

export async function deleteDocumentMaster(http: HttpClient, docCode: string): Promise<void> {
  await http.request<void>(`/api/v1/doc/masters/${encodeURIComponent(docCode)}`, {
    method: "DELETE",
  });
}

export async function deleteDocumentMasters(http: HttpClient, docCodes: string[]): Promise<void> {
  await http.request<void>("/api/v1/doc/masters/range", {
    method: "DELETE",
    body: docCodes,
  });
}

export async function createDocumentMaster(
  http: HttpClient,
  body: {
    docCode: string;
    titleVi: string;
    docTypeId: string;
    docKind: string;
    orgUnitId?: string | null;
    legacyCodes?: string;
    systemFormKey?: string;
  },
): Promise<DocumentMasterRow> {
  return http.request<DocumentMasterRow>("/api/v1/doc/masters", { method: "POST", body });
}

export async function updateDocumentMaster(
  http: HttpClient,
  docCode: string,
  body: {
    titleVi: string;
    orgUnitId?: string | null;
    legacyCodes?: string | null;
    systemFormKey?: string | null;
    retentionYears?: number;
    requiresAck?: boolean;
  },
): Promise<DocumentMasterRow> {
  return http.request<DocumentMasterRow>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}`,
    { method: "PUT", body },
  );
}

export async function setDocumentMasterStatus(
  http: HttpClient,
  docCode: string,
  status: string,
): Promise<DocumentMasterRow> {
  return http.request<DocumentMasterRow>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/status`,
    { method: "POST", body: { status } },
  );
}

export async function createDocumentVersion(
  http: HttpClient,
  docCode: string,
): Promise<DocumentVersionRow> {
  return http.request<DocumentVersionRow>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/versions`,
    { method: "POST", body: {} },
  );
}

export async function replaceVersionFields(
  http: HttpClient,
  docCode: string,
  versionNo: number,
  fields: Array<{
    seq: number;
    label: string;
    kind: string;
    isRequired?: boolean;
    optionsJson?: string | null;
    minValue?: number | null;
    maxValue?: number | null;
    step?: number | null;
    uomLabel?: string | null;
    requiredOnFail?: boolean;
    failDependsOnFieldKey?: string | null;
  }>,
): Promise<DocumentVersionRow> {
  return http.request<DocumentVersionRow>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/versions/${versionNo}/fields`,
    { method: "PUT", body: { fields } },
  );
}

export async function makeVersionEffective(
  http: HttpClient,
  docCode: string,
  versionNo: number,
): Promise<DocumentVersionRow> {
  return http.request<DocumentVersionRow>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/versions/${versionNo}/effective`,
    { method: "POST", body: {} },
  );
}

export type FormLayoutDto = { layoutJson: string | null };

export async function getVersionLayout(
  http: HttpClient,
  docCode: string,
  versionNo: number,
): Promise<FormLayoutDto> {
  return http.request<FormLayoutDto>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/versions/${versionNo}/layout`,
  );
}

export async function replaceVersionLayout(
  http: HttpClient,
  docCode: string,
  versionNo: number,
  layoutJson: string | null,
): Promise<FormLayoutDto> {
  return http.request<FormLayoutDto>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/versions/${versionNo}/layout`,
    { method: "PUT", body: { layoutJson } },
  );
}

export type PreviewHtmlDto = { html: string; contentType: string };

export async function previewDocumentVersion(
  http: HttpClient,
  docCode: string,
  versionNo: number,
  body?: {
    sampleValues?: Array<{
      fieldKey: string;
      valueText?: string | null;
      valueNumber?: number | null;
      valueBool?: boolean | null;
    }>;
    plantCode?: string;
    workDate?: string;
    layoutJson?: string | null;
  },
): Promise<PreviewHtmlDto> {
  return http.request<PreviewHtmlDto>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/versions/${versionNo}/preview`,
    { method: "POST", body: body ?? {} },
  );
}

export async function cloneDocumentMaster(
  http: HttpClient,
  docCode: string,
  body: { newDocCode: string; titleVi?: string | null },
): Promise<DocumentMasterRow> {
  return http.request<DocumentMasterRow>(
    `/api/v1/doc/masters/${encodeURIComponent(docCode)}/clone`,
    { method: "POST", body },
  );
}

export type OpTemplateLink = {
  code: string;
  nameVi: string;
  documentMasterCode: string | null;
  status: string;
};

export async function listOperationTemplatesByDoc(
  http: HttpClient,
  documentMasterCode: string,
): Promise<OpTemplateLink[]> {
  const rows = await http.request<OpTemplateLink[]>(
    "/api/v1/eng/operation-templates?status=Active",
  );
  return rows.filter(
    (r) =>
      r.documentMasterCode?.toUpperCase() === documentMasterCode.toUpperCase(),
  );
}

export async function listFormInstances(
  http: HttpClient,
  params: ListQueryParams & {
    docCode?: string;
    workDate?: string;
    batchId?: string;
  },
): Promise<PagedResult<FormInstanceSummary>> {
  const qs = buildQueryString({
    ...toApiQueryParams(params),
    ...(params.docCode || params.filters.docCode
      ? { docCode: params.docCode ?? params.filters.docCode }
      : {}),
    ...(params.workDate || params.filters.workDate
      ? { workDate: params.workDate ?? params.filters.workDate }
      : {}),
    ...(params.batchId || params.filters.batchId
      ? { batchId: params.batchId ?? params.filters.batchId }
      : {}),
  });
  const dto = await http.request<ApiPagedResult<FormInstanceSummary>>(
    `/api/v1/doc/instances${qs}`,
  );
  return mapApiPagedResult(dto, (r) => r);
}

export async function getOrganizationProfile(http: HttpClient): Promise<OrganizationProfile> {
  return http.request<OrganizationProfile>("/api/v1/sys/organization");
}

export async function updateOrganizationProfile(
  http: HttpClient,
  body: {
    legalName: string;
    taxId?: string | null;
    address?: string | null;
    brandName?: string | null;
    printFooterText?: string | null;
    bankName?: string | null;
    bankBranch?: string | null;
    bankAccountNumber?: string | null;
    bankAccountName?: string | null;
  },
): Promise<OrganizationProfile> {
  return http.request<OrganizationProfile>("/api/v1/sys/organization", {
    method: "PUT",
    body,
  });
}

export async function uploadOrganizationLogo(
  http: HttpClient,
  file: File,
): Promise<OrganizationProfile> {
  const form = new FormData();
  form.append("file", file);
  return http.request<OrganizationProfile>("/api/v1/sys/organization/logo", {
    method: "POST",
    body: form,
    formData: true,
  });
}
